import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Button, Input } from "@/shared";
import { useConfig } from "@/hooks";
import helpCoverageRegistry from "./coverage/coverage.registry";
import { isFeatureEnabled } from "./docs.contract";
import { getFeatureHelpPath } from "./help-links";

const OPEN_HELP_HUB_EVENT = "attune:help-hub-open";
const OPEN_TUTORIAL_EVENT = "attune:tutorial-open";

export function HelpHubModal() {
  const navigate = useNavigate();
  const { configs } = useConfig();
  const featureFlags = configs?.featureFlags || {};

  const [visible, setVisible] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const handleOpen = () => setVisible(true);
    window.addEventListener(OPEN_HELP_HUB_EVENT, handleOpen);
    return () => {
      window.removeEventListener(OPEN_HELP_HUB_EVENT, handleOpen);
    };
  }, []);

  const featureCards = useMemo(() => {
    return helpCoverageRegistry
      .filter((item) => isFeatureEnabled(featureFlags, item.featureKey))
      .filter((item) => {
        if (!query) return true;
        const search = query.toLowerCase().trim();
        return (
          item.title.toLowerCase().includes(search) ||
          item.description.toLowerCase().includes(search) ||
          item.id.toLowerCase().includes(search)
        );
      });
  }, [featureFlags, query]);

  const openHelpSection = (item) => {
    setVisible(false);
    navigate(getFeatureHelpPath(item));
  };

  return (
    <Modal
      visible={visible}
      setVisible={setVisible}
      onClose={() => setVisible(false)}
      size="xl"
      title="Help & Onboarding Hub"
      hideFooter
      contentWrapperClass="p-4 md:p-6 space-y-4"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Start onboarding, open comprehensive feature guides, or jump directly
          to the working module in the app.
        </p>

        <div className="flex flex-wrap gap-2">
          <Button
            type="primary"
            size="sm"
            onClick={() => {
              setVisible(false);
              window.dispatchEvent(
                new CustomEvent(OPEN_TUTORIAL_EVENT, {
                  detail: { type: "full" },
                })
              );
            }}
          >
            Start onboarding walkthrough
          </Button>
          <Button
            type="alternative"
            size="sm"
            onClick={() => {
              setVisible(false);
              navigate("/help/features");
            }}
          >
            Open Feature Docs
          </Button>
        </div>

        <Input
          id="help-hub-search"
          value={query}
          onChange={({ value }) => setQuery(value)}
          placeholder="Search guides and features"
          label="Find a guide"
          size="sm"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[420px] overflow-y-auto pr-1">
          {featureCards.map((item) => (
            <div key={item.id} className="rounded-lg border border-gray-200 bg-white p-3">
              <h4 className="text-sm font-semibold text-gray-900">{item.title}</h4>
              <p className="mt-1 text-xs text-gray-600">{item.description}</p>
              <div className="mt-2 text-[11px] text-gray-500">
                Status: {item.guideStatus} · Screenshots: {item.screenshotStatus}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Button
                  type="primary"
                  size="sm"
                  onClick={() => openHelpSection(item)}
                >
                  Open help section
                </Button>
                <Button
                  type="alternative"
                  size="sm"
                  onClick={() => {
                    setVisible(false);
                    navigate(item.inAppPath);
                  }}
                >
                  Open in app
                </Button>
              </div>
            </div>
          ))}
          {featureCards.length === 0 && (
            <div className="col-span-1 md:col-span-2 rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500">
              No guides match your search.
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default HelpHubModal;
